import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, LogOut, Menu, Search, ShieldCheck, User } from "lucide-react";
import { MenuSheet } from "./MenuSheet";
import { SearchOverlay } from "./SearchOverlay";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { scrollToId } from "./SmoothScroll";
import { LangToggle, useT } from "../lib/i18n";

const NAV = [["nav.mixes", "mixes"], ["nav.tools", "tools"], ["nav.fx", "fx-deck"]];

export const Header = () => {
  const { user, openAuth, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useT();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);

  const go = (id) => {
    if (loc.pathname !== "/") {
      nav("/");
      setTimeout(() => scrollToId(id), 400);
    } else scrollToId(id);
  };

  return (
    <motion.header
      data-testid="app-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#04070D]/70 border-b border-white/10"
    >
      <div className="max-w-[1500px] mx-auto px-4 lg:px-10 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button data-testid="menu-hamburger-button" onClick={() => setMenu(true)} aria-label={t("menu.open")} className="p-2 -ml-2 text-white hover:text-[#1FD1FF] transition-colors"><Menu className="w-6 h-6" /></button>
          <Link to="/" data-testid="brand-logo"><Logo /></Link>
        </div>
        <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-[0.2em]">
          {NAV.map(([label, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)} className="text-[#A8B3C4] hover:text-[#1FD1FF] transition-colors">
              {t(label)}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="search-open-button" onClick={() => setSearch(true)} aria-label={t("search.open")}
            className="w-10 h-10 rounded-full border border-white/25 grid place-items-center text-white hover:border-[#1FD1FF] hover:text-[#1FD1FF] transition-colors"><Search className="w-4 h-4" /></button>
          <LangToggle />
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="admin-portal-button" className="flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest border border-[#1FD1FF]/40 text-[#1FD1FF] hover:bg-[#1FD1FF] hover:text-black transition-colors">
              <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.studio")}</span>
            </Link>
          )}
          {user ? (
            <>
              <Link to="/favoritos" data-testid="nav-favorites-link" className="flex items-center gap-1.5 px-2.5 py-2 font-mono text-xs uppercase tracking-widest text-[#A8B3C4] hover:text-[#1FD1FF] transition-colors" aria-label={t("nav.favs")}>
                <Heart className="w-4 h-4" /><span className="hidden lg:inline">{t("nav.favs")}</span>
              </Link>
              <span data-testid="header-user-name" className="hidden sm:flex items-center gap-1.5 font-mono text-xs text-[#A8B3C4]"><User className="w-3.5 h-3.5" />{user.name}</span>
              <button data-testid="logout-button" onClick={logout} className="p-2 text-[#A8B3C4] hover:text-[#1FD1FF] transition-colors" aria-label="Log out"><LogOut className="w-4 h-4" /></button>
            </>
          ) : (
            <>
              <button data-testid="fan-login-button" onClick={() => openAuth("login")} className="hidden sm:block px-3 py-2 font-mono text-xs uppercase tracking-widest text-[#A8B3C4] hover:text-white transition-colors">{t("nav.login")}</button>
              <button data-testid="fan-signup-button" onClick={() => openAuth("signup")} className="px-4 py-2 font-mono text-xs uppercase tracking-widest bg-[#1FD1FF] text-black hover:bg-white transition-colors">{t("nav.join")}</button>
            </>
          )}
        </div>
      </div>
      <MenuSheet open={menu} onClose={() => setMenu(false)} />
      <SearchOverlay open={search} onClose={() => setSearch(false)} />
    </motion.header>
  );
};
